import { EditorContent, useEditor } from '@tiptap/react'
import { IconEye, IconPencil } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { forumTipTapExtensions } from '@/components/richtext/tiptap-extensions'
import { RichEditorToolbar } from '@/components/richtext/RichEditorToolbar'
import { cn } from '@/lib/cn'
import { RichContent } from '@/components/richtext/RichContent'

type Mode = 'write' | 'preview'

export function RichEditor({
  initialJSON,
  onChangeJSON,
  className,
}: {
  initialJSON?: unknown
  onChangeJSON: (json: unknown) => void
  className?: string
}) {
  const [mode, setMode] = useState<Mode>('write')

  const extensions = useMemo(() => forumTipTapExtensions(), [])

  const editor = useEditor({
    extensions,
    immediatelyRender: false,
    content: (initialJSON as never) ?? { type: 'doc', content: [{ type: 'paragraph' }] },
    editorProps: {
      attributes: {
        class: cn(
          'nk-tiptap-editor ProseMirror max-w-none px-3 py-3 text-[15px] leading-relaxed text-nk-navy outline-none',
          'min-h-[220px] focus:outline-none',
          '[&_h1]:text-2xl [&_h1]:font-black [&_h1]:tracking-tight [&_h2]:text-xl [&_h2]:font-black [&_h2]:tracking-tight',
          '[&_h3]:text-lg [&_h3]:font-extrabold [&_h3]:tracking-tight',
          '[&_a]:text-nk-sea [&_blockquote]:border-l-4 [&_blockquote]:border-nk-sea [&_blockquote]:bg-nk-sand/80 [&_blockquote]:py-0.5 [&_blockquote]:pl-3',
          '[&_code]:rounded [&_code]:bg-nk-sand [&_code]:px-1 [&_code]:text-[13px] [&_pre]:rounded-lg [&_pre]:border [&_pre]:border-nk-fog [&_pre]:bg-nk-navy [&_pre]:p-3 [&_pre]:text-[13px] [&_pre]:text-white',
          '[&_hr]:my-4 [&_hr]:border-0 [&_hr]:border-t-2 [&_hr]:border-nk-fog',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
        ),
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChangeJSON(ed.getJSON())
    },
  })

  useEffect(() => {
    if (!editor) return
    if (initialJSON) editor.commands.setContent(initialJSON as never, false)
  }, [editor, initialJSON])

  if (!editor) return <p className="text-[13px] text-nk-slate">Starting editor…</p>

  const doc = editor.getJSON()

  return (
    <div className={cn('space-y-2', className)}>
      <div className="overflow-hidden rounded-lg border-2 border-nk-fog bg-white shadow-sm ring-1 ring-black/5">
        <div className="flex flex-col border-b-2 border-nk-fog bg-nk-panel md:flex-row md:items-stretch">
          <div className="flex min-w-0 flex-1" role="tablist" aria-label="Editor mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'write'}
              className={cn(
                'inline-flex flex-1 items-center justify-center gap-2 px-4 py-3 text-[13px] font-extrabold uppercase tracking-wide',
                mode === 'write'
                  ? 'bg-nk-navy text-white'
                  : 'bg-transparent text-nk-navy hover:bg-white/60',
                'focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nk-sea',
              )}
              onClick={() => setMode('write')}
            >
              <IconPencil className="h-4 w-4 shrink-0" aria-hidden />
              Write
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'preview'}
              className={cn(
                'inline-flex flex-1 items-center justify-center gap-2 border-t-2 border-nk-fog px-4 py-3 text-[13px] font-extrabold uppercase tracking-wide md:border-l-2 md:border-t-0',
                mode === 'preview'
                  ? 'bg-nk-navy text-white'
                  : 'bg-transparent text-nk-navy hover:bg-white/60',
                'focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nk-sea',
              )}
              onClick={() => setMode('preview')}
            >
              <IconEye className="h-4 w-4 shrink-0" aria-hidden />
              Preview
            </button>
          </div>
        </div>

        {mode === 'write' ? <RichEditorToolbar editor={editor} /> : null}

        {mode === 'write' ? (
          <div className="bg-white">
            <EditorContent editor={editor} />
          </div>
        ) : (
          <div className="border-t border-nk-fog bg-nk-sand/40 px-3 py-4">
            <RichContent doc={doc} />
          </div>
        )}
      </div>

      <p className="text-[12px] leading-snug text-nk-slate">
        Tip: Use the toolbar for fonts, colors, alignment, and spoilers. Markdown-style shortcuts still work where
        TipTap supports them. HTTPS links only. Posts are moderated.
      </p>
    </div>
  )
}
